import React, { useEffect, useState } from "react";
import { Search } from "lucide-react";
import { api } from "@/lib/apiClient";
import Layout, { useSEO } from "@/components/Layout";
import { Section, Overline, BlueRule } from "@/components/primitives";
import { PrincipleCard } from "@/components/cards";
import NewsletterSection from "@/components/NewsletterSection";

const CATS = ["All", "Mindset", "Discipline", "Leadership", "Resilience", "Relationships", "Growth", "Purpose", "Success", "Productivity", "Character"];

export default function Principles() {
  const [principles, setPrinciples] = useState([]);
  const [cat, setCat] = useState("All");
  const [search, setSearch] = useState("");

  useSEO({ title: "Principles Library | Impactful Mindset", description: "A library of original principles on mindset, discipline, leadership, resilience and growth." });

  useEffect(() => {
    const params = {};
    if (cat !== "All") params.category = cat;
    if (search) params.search = search;
    const t = setTimeout(() => api.get("/principles", { params }).then((r) => setPrinciples(r.data)), 200);
    return () => clearTimeout(t);
  }, [cat, search]);

  return (
    <Layout>
      <Section className="pt-20 pb-10">
        <Overline>Principles Library</Overline>
        <h1 className="font-serif-display text-4xl sm:text-5xl font-bold mt-4 text-[#111827]">Ideas we return to</h1>
        <BlueRule className="mt-6" />
        <p className="text-lg text-slate-600 mt-6 max-w-2xl leading-relaxed">
          A growing collection of original principles—short, practical statements to think with and act on.
        </p>
      </Section>

      <Section className="pb-8">
        <div className="relative max-w-md mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
          <input data-testid="principles-search" value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search principles…"
            className="w-full border border-slate-300 pl-10 pr-4 py-3 focus:outline-none focus:border-[#0F2C59]" />
        </div>
        <div className="flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button key={c} data-testid={`principles-cat-${c.toLowerCase()}`} onClick={() => setCat(c)}
              className={`px-4 py-2 text-sm border transition-colors ${cat === c ? "bg-[#0F2C59] text-white border-[#0F2C59]" : "border-slate-300 text-slate-700 hover:border-[#0F2C59]"}`}>
              {c}
            </button>
          ))}
        </div>
      </Section>

      <Section className="pb-20">
        {principles.length === 0 ? (
          <div className="py-20 text-center border border-dashed border-slate-300">
            <p className="font-serif-display text-xl text-slate-500">No principles found.</p>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 gap-x-12 gap-y-12">
            {principles.map((p) => <PrincipleCard key={p.id} principle={p} />)}
          </div>
        )}
      </Section>

      <NewsletterSection />
    </Layout>
  );
}
